import {
  INCOMPLETE_VERIFICATION_STATUSES,
  INCOME_STATUSES,
  OCROLUS_FAIL_STATUSES,
  PAYROLL_CONNECTION_FAILURE_REASONS
} from "./constants";
import {
  IncomeSourceMappedStatus,
  NetIncomeAnalysis,
  PayrollConnectionFailureReason
} from "./types";
import { schemeCategory10 } from "d3-scale-chromatic";
import { CombinedIncome, MappedIncome } from "./types/income";
import { Warning } from "./types/verified/common";
import { getIncomeJobName, isIncomeVerificationSkipped } from "./utils";
import { toMoney } from "shared/utils/currency";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";
import {
  InscribeRiskLevel,
  parseRiskLevel
} from "views/applications-id-report/utils/parseRiskLevel";
import { Badge, BadgeSize } from "shared/components";
import { InvalidData } from "./components/InvalidData";
import {
  DocumentVerificationProvider,
  IncomeStatus,
  IncomeType,
  VerifyMethodType
} from "shared/types";
import { BankStatementTableRowData } from "./slices/TablesSlice/components/BankStatementTable/types";
import { VerifiedBankStatement } from "./types/verified/bank-statment";
import { DateTime } from "luxon";
import { FRAUD_RISK_BADGE_MAP, resolveFraudRisk } from "./utils/fraudRisk";
import { DocumentCell } from "./components/DocumentCell";
import { resolveDocumentProblems } from "./utils/documentProblems";
import { has } from "shared/utils";

export const defaultTableColumns = [
  {
    header: "Verified income sources",
    accessor: (row) => row.source,
    widthClass: "w-full"
  },
  {
    header: "Date range",
    accessor: (row) => row.dateRange,
    widthClass: "w-full"
  },
  {
    header: "Gross monthly",
    accessor: (row) => row.grossMonthly,
    widthClass: "w-[85px] shrink-0"
  },
  {
    header: "Net monthly",
    accessor: (row) => row.netMonthly,
    widthClass: "w-[85px] shrink-0"
  }
];

const documentColumn = {
  header: "Document",
  accessor: (row, index) => (
    <DocumentCell
      filename={row.asset?.asset?.filename ?? `Document #${index + 1}`}
      problems={resolveDocumentProblems(row)}
      isProcessing={INCOMPLETE_VERIFICATION_STATUSES.includes(
        row.sourceStatus ?? row.source_status ?? row.status
      )}
    />
  ),
  widthClass: "w-[220px] shrink-0"
};

const dateRangeColumn = {
  header: "Date range",
  accessor: (row) => {
    let startDate: string;
    let endDate: string;

    if (row?.verifyType === VerifyMethodType.BankStatement) {
      startDate = row?.additional_data?.bank_statement_start_date;
      endDate = row?.additional_data?.bank_statement_end_date;
    } else {
      startDate = row?.paystub_details?.pay_period_start_date;
      endDate = row?.paystub_details?.pay_period_end_date;
    }

    return (
      <p className="text-content-500 text-[13px] leading-[22px]">
        {!startDate && !endDate ? (
          "-"
        ) : (
          <>
            {startDate ? (
              DateTime.fromISO(startDate).toFormat("MMM d, yyyy")
            ) : (
              <InvalidData text="Invalid start date" />
            )}
            <br />
            {endDate ? (
              DateTime.fromISO(endDate).toFormat("MMM d, yyyy")
            ) : (
              <InvalidData text="Invalid end date" />
            )}
          </>
        )}
      </p>
    );
  },
  widthClass: "w-full"
};

const yearColumn = {
  header: "Year",
  accessor: (row) => (
    <p className="text-content-200 font-medium text-sm">
      {row?.year ? String(row.year) : "-"}
    </p>
  ),
  widthClass: "w-full text-right"
};

const grossPayColumn = {
  header: "Gross pay",
  accessor: (row) => {
    if (row?.verifyType === VerifyMethodType.BankStatement) {
      const { totals, incomeId } = row as BankStatementTableRowData;
      const { gross_total } = (incomeId && totals?.[incomeId]) || {};

      return gross_total ? toMoney(gross_total) : "-";
    }

    return row?.total_gross_pay_amount
      ? toMoney(row?.total_gross_pay_amount)
      : "-";
  },
  widthClass: "w-[100px] shrink-0 text-right"
};

const netPayColumn = {
  header: "Net pay",
  accessor: (row) => {
    if (row?.verifyType === VerifyMethodType.BankStatement) {
      const { totals, incomeId } = row as BankStatementTableRowData;
      const { net_total } = (incomeId && totals?.[incomeId]) || {};

      return net_total ? toMoney(net_total) : "-";
    }

    return row.total_net_pay_amount ? toMoney(row.total_net_pay_amount) : "-";
  },
  widthClass: "w-[100px] shrink-0 text-right"
};

const fraudRiskColumn = {
  header: "Fraud risk",
  accessor: (row) => {
    const riskLevel = resolveFraudRisk(row);

    if (!riskLevel) return <p className="text-content-300 text-sm">-</p>;

    const config = FRAUD_RISK_BADGE_MAP[riskLevel];

    return (
      <Badge
        variant={config.variant}
        size={BadgeSize.SM}
        className="items-center"
      >
        {config.title}
      </Badge>
    );
  },
  widthClass: "w-full text-left pl-4"
};

export const documentTypeTableColumns = [
  documentColumn,
  dateRangeColumn,
  netPayColumn,
  grossPayColumn,
  fraudRiskColumn
];

export const w2TableColumns = [
  documentColumn,
  yearColumn,
  netPayColumn,
  grossPayColumn,
  fraudRiskColumn
];

export const documentWithoutDatesTableColumns = [
  documentColumn,
  netPayColumn,
  grossPayColumn,
  fraudRiskColumn
];

export function formatMonths(months: number): string {
  if (months < 12) return `${months} month${months === 1 ? "" : "s"}`;

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  const parts: string[] = [];

  if (years) parts.push(`${years} year${years > 1 ? "s" : ""}`);
  if (remainingMonths)
    parts.push(`${remainingMonths} month${remainingMonths > 1 ? "s" : ""}`);

  return parts.join(", ");
}

export function mapNetIncomeAnalysis(data: NetIncomeAnalysis): MappedIncome[] {
  const monthMap = new Map<string, MappedIncome>();
  const companies = Object.keys(data);
  const colorMap = Object.fromEntries(
    companies.map((company, index) => [
      company,
      schemeCategory10[index % schemeCategory10.length]
    ])
  );

  Object.entries(data).forEach(([company, months]) => {
    Object.entries(months).forEach(([month, incomeData]) => {
      let income = 0;

      if (!monthMap.has(month))
        monthMap.set(month, {
          month,
          total: 0,
          income: []
        });

      const entry = monthMap.get(month)!;

      if (typeof incomeData === "object" && "total" in incomeData) {
        income = incomeData.total;
        incomeData.sources.forEach((source) => {
          entry.income.push({
            month: source.pay_date,
            company: source.description,
            income: source.net_amount,
            color: colorMap[company]
          });
        });
      } else {
        income = incomeData;

        entry.income.push({
          month,
          company,
          income,
          color: colorMap[company]
        });
      }

      entry.total += income;
    });
  });

  return Array.from(monthMap.values()).sort((a, b) =>
    b.month.localeCompare(a.month)
  );
}

export const scrollToIncomeSource = (incomeId: number) => {
  const element = document.getElementById(
    `combined-income-section-${incomeId}`
  );
  if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
};

const warningMessage = " has verification warnings on one or more documents";
const rejectedMessage =
  " was not successfully verified because all documents were rejected";

const RISK_LEVEL_SEVERITY: Record<InscribeRiskLevel, number> = {
  NOT_APPLICABLE: 0,
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3
};

const INSCRIBE_DOC_SOURCE_TYPES = [
  VerifyMethodType.Paystub,
  VerifyMethodType.W2,
  VerifyMethodType.BenefitsStatement,
  VerifyMethodType.BankStatement
] as string[];

type InscribeItemLike = {
  provider?: DocumentVerificationProvider;
  is_archived?: boolean;
  income_source_data?: {
    additional_data?: {
      risk_level?: InscribeRiskLevel;
    };
  };
};

const pickWorstRiskLevel = (
  levels: (InscribeRiskLevel | undefined | null)[]
): InscribeRiskLevel | undefined =>
  levels.reduce<InscribeRiskLevel | undefined>((worst, cur) => {
    if (!cur) return worst;
    if (!worst) return cur;
    return RISK_LEVEL_SEVERITY[cur] > RISK_LEVEL_SEVERITY[worst] ? cur : worst;
  }, undefined);

const collectBankStatementRiskLevels = (
  income: CombinedIncome,
  bankStatements?: Record<number, VerifiedBankStatement>
): (InscribeRiskLevel | undefined)[] => {
  if (!bankStatements) return [];

  const sourceIds = new Set<number>(income.income_sources_ids ?? []);
  (income.items as { income_source_id?: number }[]).forEach((item) => {
    if (item.income_source_id !== undefined)
      sourceIds.add(item.income_source_id);
  });

  return Array.from(sourceIds).map(
    (id) =>
      bankStatements[id]?.additional_data?.risk_level as
        | InscribeRiskLevel
        | undefined
  );
};

const resolveInscribeIncomeStatus = (
  income: CombinedIncome,
  activeItems: InscribeItemLike[],
  bankStatements?: Record<number, VerifiedBankStatement>
): "Rejected" | "Warning" | "Accepted" => {
  const riskLevels =
    income.verified_source_type === VerifyMethodType.BankStatement
      ? collectBankStatementRiskLevels(income, bankStatements)
      : activeItems.map(
          (item) => item.income_source_data?.additional_data?.risk_level
        );
  const riskStatus = parseRiskLevel(pickWorstRiskLevel(riskLevels));

  const hasVerifiedAmounts = Boolean(
    has(income.verified_monthly_amounts.gross) &&
    has(income.verified_monthly_amounts.net)
  );

  if (
    !hasVerifiedAmounts &&
    income.verified_source_type === VerifyMethodType.BenefitsStatement
  )
    return "Rejected";

  return riskStatus ?? INCOME_STATUSES[income.status];
};

export const mapIncomeSourceStatus = (
  income: CombinedIncome,
  bankStatements?: Record<number, VerifiedBankStatement>
): IncomeSourceMappedStatus => {
  const incomeStatusMeta = {
    incomeId: income.id,
    incomeName: getIncomeJobName(income.additional_data),
    type: income.type
  };

  if (income.type === IncomeType.OfferLetter)
    return {
      ...incomeStatusMeta,
      description: `"${incomeStatusMeta.incomeName}" was not successfully verified because offer letters require manual review`,
      status: "Rejected"
    };

  if (isIncomeVerificationSkipped(income))
    return {
      ...incomeStatusMeta,
      description: "Income verification was skipped",
      status: "Rejected"
    };

  if ([IncomeStatus.Pending, IncomeStatus.Processing].includes(income.status))
    return {
      ...incomeStatusMeta,
      description: "",
      status: "Started"
    };

  if (income.status === IncomeStatus.FinishedByOverride)
    return {
      ...incomeStatusMeta,
      description: "Verification manually marked as complete",
      status: "Overridden"
    };

  let rejectedDocumentsCount = 0;
  let documentsStatus: "Rejected" | "Warning" | "Accepted" | undefined;
  let description = `"${incomeStatusMeta.incomeName}"`;

  const activeItems = (income.items as InscribeItemLike[]).filter(
    (item) => !item.is_archived
  );

  const isInscribeDocSource =
    INSCRIBE_DOC_SOURCE_TYPES.includes(income.verified_source_type) &&
    activeItems.some(
      (item) => item.provider === DocumentVerificationProvider.Inscribe
    );

  if (isInscribeDocSource) {
    documentsStatus = resolveInscribeIncomeStatus(
      income,
      activeItems,
      bankStatements
    );

    if (documentsStatus === "Accepted") description = "";
    else if (documentsStatus === "Warning") description += warningMessage;
    else if (documentsStatus === "Rejected") description += rejectedMessage;

    return { ...incomeStatusMeta, status: documentsStatus, description };
  }

  income.items.forEach((item) => {
    if (
      (item.income_source_type === "paystub" ||
        item.income_source_type === "w2") &&
      item.data?.document?.warnings?.length
    ) {
      item.data.document.warnings.forEach((warning: Warning) => {
        const isDocumentRejected =
          OCROLUS_FAIL_STATUSES.includes(warning.detect_status) ||
          parseDocStatus(
            warning.form_analysis?.[0]?.form_authenticity?.score ?? 0
          ) === "Rejected";

        if (isDocumentRejected) rejectedDocumentsCount += 1;
      });

      // If every item is rejected - the income is considered to be Rejected, otherwise - it's a Warning
      if (
        !item.data.document.income_sources?.length &&
        item.data.document.warnings?.length === rejectedDocumentsCount
      ) {
        documentsStatus = "Rejected";
        description += rejectedMessage;
      } else {
        documentsStatus = "Warning";
        description += warningMessage;
      }
    }
    // No item warnings - income is Accepted
    else {
      documentsStatus = "Accepted";
      description = "";
    }
  });

  return {
    ...incomeStatusMeta,
    status: documentsStatus || INCOME_STATUSES[income.status],
    description
  };
};

export const mapUnverifiedIncomeStatus = (income: CombinedIncome) => {
  const incomeName = getIncomeJobName(income.additional_data);

  return {
    incomeId: income.id,
    incomeName,
    description: `${incomeName} was not successfully verified because one or more documents were rejected`,
    status: "Rejected"
  };
};

// The API clears the reason as soon as the applicant starts reconnecting, so anything here is a
// connection that is still failed.
export const mapPayrollConnectionFailure = (
  reason?: PayrollConnectionFailureReason | null
) => (reason ? PAYROLL_CONNECTION_FAILURE_REASONS[reason] : undefined);
